import { memo, useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  ACHIEVEMENT_FALLBACK_TYPES,
  ACHIEVEMENT_TIERS,
} from '@didaskill/domain/user-achievements/constants';
import type { UserAchievement } from '@didaskill/domain/user-achievements/types';
import { Drawer } from '@didaskill/ui/design-system';
import { achievementMessages, globalMessages } from '@didaskill/ui/intl';

import messages from './AchievementsDrawer.messages';
import * as styles from './AchievementsDrawer.styles';
import { AchievementsDrawerIntro } from './achievements-drawer-intro/AchievementsDrawerIntro';
import { DisplayedAchievement } from './displayed-achievement/DisplayedAchievement';

const DISPLAY_INTRO_THRESHOLD = 3;

export type AchievementsDrawerProps = {
  achievements: UserAchievement[];
  isOpen?: boolean;
  onRequestClose: () => Promise<void>;
  onRedirect: () => void;
};

const AchievementsDrawerBase = ({
  achievements,
  isOpen = false,
  onRequestClose,
  onRedirect,
}: AchievementsDrawerProps) => {
  const [step, setStep] = useState(0);

  const withIntroOnly = useMemo(
    () => achievements.length >= DISPLAY_INTRO_THRESHOLD,
    [achievements.length],
  );

  const stepsCount = useMemo(
    () => (withIntroOnly ? 1 : achievements.length),
    [achievements.length, withIntroOnly],
  );

  const currentAchievement = useMemo(() => achievements[step], [achievements, step]);

  const onClickContinue = useCallback(() => {
    if (withIntroOnly || step === stepsCount - 1) {
      onRequestClose();

      return;
    }
    setStep((prevStep) => prevStep + 1);
  }, [stepsCount, onRequestClose, step, withIntroOnly]);

  const currentAchievementCatchphrase = useMemo(
    () => achievementMessages[currentAchievement.type][currentAchievement.tier].CATCH_PHRASE,
    [currentAchievement],
  );

  // Fallback to default achievement if no catchphrase is available
  const displayedAchievement = useMemo(
    () =>
      currentAchievementCatchphrase
        ? currentAchievement
        : {
            type: ACHIEVEMENT_FALLBACK_TYPES.DEFAULT,
            tier: ACHIEVEMENT_TIERS.GOLD,
          },
    [currentAchievement, currentAchievementCatchphrase],
  );

  const onClickSeeAchievements = useCallback(async () => {
    onRequestClose();

    onRedirect();
  }, [onRedirect, onRequestClose]);

  return (
    <Drawer position={Drawer.POSITIONS.FULL_SCREEN} isOpen={isOpen}>
      <div css={styles.achievementsDrawer}>
        {withIntroOnly && step === 0 ? (
          <AchievementsDrawerIntro />
        ) : (
          <DisplayedAchievement achievement={displayedAchievement} />
        )}

        <Drawer.Actions>
          <Drawer.Action outline onClick={onClickSeeAchievements}>
            <FormattedMessage {...messages.SEE_ACHIEVEMENTS} />
          </Drawer.Action>

          <Drawer.Action onClick={onClickContinue} variant={Drawer.Action.VARIANTS.PRIMARY}>
            <FormattedMessage {...globalMessages.CONTINUE} />
          </Drawer.Action>
        </Drawer.Actions>
      </div>
    </Drawer>
  );
};

AchievementsDrawerBase.displayName = 'AchievementDrawer';

export const AchievementsDrawer = memo(AchievementsDrawerBase);
